import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Input } from "../components/ui/input";
import { PackageSearch } from "lucide-react";
import api from "../lib/api";
import { useLang, localizeEta } from "../lib/i18n";

export default function OrderTrackingModal({ open, onOpenChange }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ order_number: "", phone: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setResult(null);
      setError("");
      setForm({ order_number: "", phone: "" });
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get("/orders/track", { params: form });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || t("tracking.not_found"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        style={{ background: "#FFFBF2", border: "2px solid #0F172A", borderRadius: 20 }}
        data-testid="order-tracking-modal"
      >
        <VisuallyHidden>
          <DialogTitle>{t("tracking.title")}</DialogTitle>
          <DialogDescription>{t("tracking.desc")}</DialogDescription>
        </VisuallyHidden>

        {!result ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <PackageSearch size={22} />
              <h3 className="font-display text-xl font-bold">{t("tracking.title")}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">{t("tracking.desc")}</p>
            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="tracking-form">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-semibold">{t("tracking.order_number")}</span>
                <Input
                  required
                  dir="ltr"
                  value={form.order_number}
                  onChange={(e) => setForm((s) => ({ ...s, order_number: e.target.value }))}
                  placeholder="PM-260101-ABCDEF"
                  data-testid="tracking-order-number-input"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-semibold">{t("tracking.phone")}</span>
                <Input
                  required
                  dir="ltr"
                  value={form.phone}
                  onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                  placeholder="01024049798"
                  data-testid="tracking-phone-input"
                />
              </label>
              {error && (
                <div className="text-sm font-semibold text-red-600" data-testid="tracking-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="neo-btn py-2.5 font-bold text-white disabled:opacity-60"
                style={{ background: "var(--pm-dog)" }}
                data-testid="tracking-submit-btn"
              >
                {loading ? t("tracking.searching") : t("tracking.submit")}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="tracking-result">
            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
              <div className="font-display text-xl font-bold">{result.order_number}</div>
              <span
                className="text-xs font-bold px-2 py-1 rounded-md"
                style={{ background: "#38BDF8", color: "#0F172A", border: "2px solid #0F172A" }}
              >
                {result.status}
              </span>
            </div>
            <div className="space-y-1.5 text-sm neo-flat p-4">
              <Row label={t("tracking.payment_status")} value={result.payment_status} />
              <Row label={t("tracking.shipping_status")} value={result.shipping_status} />
              <Row
                label={t("confirm.shipping")}
                value={`${result.shipping_method} (${localizeEta(result.shipping_eta, lang)})`}
              />
              <Row label={t("confirm.subtotal")} value={`${result.subtotal.toFixed(0)} EGP`} />
              <div className="flex justify-between font-display text-base font-bold pt-2 border-t border-dashed border-slate-300 mt-1">
                <span>{t("confirm.total")}</span>
                <span>{result.total.toFixed(0)} EGP</span>
              </div>
            </div>
            <button
              onClick={() => setResult(null)}
              className="neo-btn w-full mt-3 py-2.5 bg-white font-semibold"
              data-testid="tracking-search-again-btn"
            >
              {t("tracking.search_again")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-end">{value || "—"}</span>
    </div>
  );
}
